// @noflow
import { bulbTypes, deviceTypes } from '~/core/constants/devices';
import { consumerPropertyTypes as cpt, templateTypes } from '~/core/constants/recipes';

import { fromJS } from 'immutable';
import messages from './intl';
import { rangeStep } from 'lodash/fp';

const {
    ACTIVE_PLUG,
    BULB,
    COLOR_TUNEABLE_LIGHT,
    COLOR_LIGHT,
    CONTACT_SENSOR,
    HEATING,
    MOTION_SENSOR,
    TUNEABLE_LIGHT,
    WARM_WHITE_LIGHT,
    HIVE_BUTTON
} = deviceTypes;

export const producersList = [CONTACT_SENSOR, MOTION_SENSOR, HIVE_BUTTON];

export const consumerProperties = {
    [cpt.duration]: {
        type: cpt.duration,
        label: messages.for,
        optionsList: [
            { label: messages.indefinitely, value: -1 },
            { label: messages._30seconds, value: 30 },
            { label: messages._1minute, value: 60 },
            { label: messages._3minutes, value: 180 },
            { label: messages._5minutes, value: 300 },
            { label: messages._10minutes, value: 600 },
            { label: messages._15minutes, value: 900 }
        ]
    },
    [cpt.changeLightOnOff]: {
        type: cpt.changeLightOnOff,
        label: messages.lightLabel,
        field: 'state',
        optionsList: [{ label: messages.lightOn, value: 'ON' }, { label: messages.lightOff, value: 'OFF' }]
    },
    [cpt.changePlugOnOff]: {
        type: cpt.changePlugOnOff,
        label: messages.plugLabel,
        field: 'state',
        optionsList: [{ label: messages.plugOn, value: 'ON' }, { label: messages.plugOff, value: 'OFF' }]
    },
    [cpt.changeHeatingMode]: {
        type: cpt.changeHeatingMode,
        label: messages.heatingModeLabel,
        field: 'mode',
        optionsList: [
            { label: messages.heatingScheduleMode, value: 'HEAT' },
            { label: messages.heatingManualMode, value: 'HEAT' },
            { label: messages.heatingOffMode, value: 'OFF' },
            { label: messages.heatingBoostMode, value: 'BOOST' }
        ],
        defaultOptionIndex: 0
    },
    [cpt.changeHeatingTarget]: {
        type: cpt.changeHeatingTarget,
        label: messages.heatingTargetLabel,
        field: 'target',
        optionsList: (() => rangeStep(0.5, 5, 32.5).map(v => ({ label: `${v}°`, value: v })))(),
        defaultOptionIndex: 22
    },
    [cpt.changeHeatingBoost]: {
        type: cpt.changeHeatingBoost,
        label: messages.heatingBoostLabel,
        field: 'boost',
        optionsList: [
            { label: messages.heatingBoostDuration030, value: 30 },
            { label: messages.heatingBoostDuration1, value: 60 },
            { label: messages.heatingBoostDuration2, value: 120 },
            { label: messages.heatingBoostDuration3, value: 180 },
            { label: messages.heatingBoostDuration4, value: 240 },
            { label: messages.heatingBoostDuration5, value: 300 },
            { label: messages.heatingBoostDuration6, value: 360 }
        ],
        defaultOptionIndex: 1
    }
};

export const getConsumerPropertiesList = (deviceType, current, recipe) => {
    const list = {
        [true]: () => [],
        [deviceType === ACTIVE_PLUG]: () => [cpt.changePlugOnOff, cpt.duration],
        [[BULB, COLOR_TUNEABLE_LIGHT, COLOR_LIGHT, TUNEABLE_LIGHT, WARM_WHITE_LIGHT].includes(deviceType)]: () => [
            cpt.changeLightOnOff,
            cpt.duration
        ],
        [deviceType === HEATING]: () => {
            if (recipe) {
                const modeAction = recipe
                    .getIn(['action', 'changes'])
                    .find(a => a.get('field') === consumerProperties[cpt.changeHeatingMode].field);
                if (modeAction.get('value') === 'BOOST') {
                    return [cpt.changeHeatingMode, cpt.changeHeatingTarget, cpt.changeHeatingBoost];
                } else if (
                    modeAction.get('value') === 'HEAT' &&
                    recipe
                        .getIn(['action', 'changes'])
                        .find(a => a.get('field') === consumerProperties[cpt.changeHeatingTarget].field)
                ) {
                    return [cpt.changeHeatingMode, cpt.changeHeatingTarget];
                }
            } else {
                const mode = current && current.find(p => p.get('type') === cpt.changeHeatingMode);
                if (mode) {
                    if (mode.get('selectedIndex') === 1) {
                        return [cpt.changeHeatingMode, cpt.changeHeatingTarget];
                    } else if (mode.get('selectedIndex') === 3) {
                        return [cpt.changeHeatingMode, cpt.changeHeatingTarget, cpt.changeHeatingBoost];
                    }
                }
            }
            return [cpt.changeHeatingMode];
        }
    }[true]();

    return list.map(p => {
        const attr = consumerProperties[p];
        let selectedIndex = attr.defaultOptionIndex || 0;
        if (recipe) {
            if (attr.type === cpt.duration) {
                selectedIndex = attr.optionsList.findIndex(
                    opt => opt.value === recipe.getIn(['action', 'duration'], -1)
                );
            } else {
                const changeAction = recipe.getIn(['action', 'changes']).find(a => a.get('field') === attr.field);
                selectedIndex = attr.optionsList.findIndex(opt => opt.value === changeAction.get('value'));
                // because both schedule and manual modes come with the same value (HEAT) we need to also look if the
                // target also exist to know which mode it is
                if (changeAction.get('value') === 'HEAT' && list.includes(cpt.changeHeatingTarget)) {
                    selectedIndex = 1;
                }
            }
        } else if (current) {
            const currentProperty = current.find(item => item.get('type') === p);
            if (currentProperty) {
                selectedIndex = currentProperty.get('selectedIndex', selectedIndex);
            }
        }
        return {
            ...consumerProperties[p],
            selectedIndex
        };
    });
};

export const isConsumerActive = (recipe, deviceType) => {
    const list = recipe.getIn(['data', 'action', 'changes']);
    return {
        [true]: () => false,
        [[ACTIVE_PLUG, BULB, COLOR_TUNEABLE_LIGHT, COLOR_LIGHT, TUNEABLE_LIGHT, WARM_WHITE_LIGHT].includes(
            deviceType
        )]: () => list.getIn([0, 'value']) === 'ON',
        [deviceType === HEATING]: () => list.find(action => action.get('field') === 'mode').get('value') !== 'OFF'
    }[true]();
};

const createAction = (field, value) =>
    fromJS({
        field,
        modifier: 'SET_TO',
        value
    });

export const updateActionsValue = recipe => {
    const list = recipe.get('consumerPropertiesList');
    let changesList = recipe.getIn(['data', 'action', 'changes']);
    recipe = list.reduce((recipe, prop) => {
        const type = prop.get('type');
        const { value } = consumerProperties[type].optionsList[prop.get('selectedIndex')];
        if (type === cpt.duration) {
            return value === -1
                ? recipe.removeIn(['data', 'action', 'duration'])
                : recipe.setIn(['data', 'action', 'duration'], value);
        } else if ([cpt.changeLightOnOff, cpt.changePlugOnOff].includes(type)) {
            changesList = changesList.setIn([0, 'value'], value);
        } else if (type === cpt.changeHeatingMode) {
            // if the mode is not boost then delete the boost duration from the list of actions
            if (prop.get('selectedIndex') !== 3) {
                const boostDurationIndex = changesList.findIndex(
                    a => a.get('field') === consumerProperties[cpt.changeHeatingBoost].field
                );
                if (boostDurationIndex !== -1) {
                    changesList = changesList.remove(boostDurationIndex);
                }
            }
            // if the mode is either schedule or off then delete the target from the list of actions
            if ([0, 2].includes(prop.get('selectedIndex'))) {
                const targetIndex = changesList.findIndex(
                    a => a.get('field') === consumerProperties[cpt.changeHeatingTarget].field
                );
                if (targetIndex !== -1) {
                    changesList = changesList.remove(targetIndex);
                }
            }
            const actionIndex = changesList.findIndex(
                a => a.get('field') === consumerProperties[cpt.changeHeatingMode].field
            );
            changesList = changesList.setIn([actionIndex, 'value'], value);
        } else if ([cpt.changeHeatingTarget, cpt.changeHeatingBoost].includes(type)) {
            const actionIndex = changesList.findIndex(a => a.get('field') === consumerProperties[type].field);
            changesList =
                actionIndex === -1
                    ? changesList.push(createAction(consumerProperties[type].field, value))
                    : changesList.setIn([actionIndex, 'value'], value);
        }
        return recipe;
    }, recipe);

    return recipe.setIn(['data', 'action', 'changes'], changesList);
};

export const templates = {
    [templateTypes.notifyMotion]: {
        isNotification: true,
        producer: {
            active: true,
            type: MOTION_SENSOR
        }
    },
    [templateTypes.notifyOpenContact]: {
        isNotification: true,
        producer: {
            active: true,
            type: CONTACT_SENSOR
        }
    },
    [templateTypes.notifyClosedContact]: {
        isNotification: true,
        producer: {
            active: false,
            type: CONTACT_SENSOR
        }
    },
    [templateTypes.modifyPlugWhenMotion]: {
        consumer: {
            propertiesList: getConsumerPropertiesList(ACTIVE_PLUG),
            types: [ACTIVE_PLUG]
        },
        producer: {
            active: true,
            type: MOTION_SENSOR
        }
    },
    [templateTypes.modifyPlugWhenOpenContact]: {
        consumer: {
            propertiesList: getConsumerPropertiesList(ACTIVE_PLUG),
            types: [ACTIVE_PLUG]
        },
        producer: {
            active: true,
            type: CONTACT_SENSOR
        }
    },
    [templateTypes.modifyPlugWhenClosedContact]: {
        consumer: {
            propertiesList: getConsumerPropertiesList(ACTIVE_PLUG),
            types: [ACTIVE_PLUG]
        },
        producer: {
            active: false,
            type: CONTACT_SENSOR
        }
    },
    [templateTypes.modifyLightWhenMotion]: {
        consumer: {
            propertiesList: getConsumerPropertiesList(BULB),
            types: bulbTypes
        },
        producer: {
            active: true,
            type: MOTION_SENSOR
        }
    },
    [templateTypes.modifyLightWhenOpenContact]: {
        consumer: {
            propertiesList: getConsumerPropertiesList(BULB),
            types: bulbTypes
        },
        producer: {
            active: true,
            type: CONTACT_SENSOR
        }
    },
    [templateTypes.modifyLightWhenClosedContact]: {
        consumer: {
            propertiesList: getConsumerPropertiesList(BULB),
            types: bulbTypes
        },
        producer: {
            active: false,
            type: CONTACT_SENSOR
        }
    },
    [templateTypes.motionSensorHeatingMode]: {
        consumer: {
            propertiesList: getConsumerPropertiesList(HEATING),
            types: [HEATING]
        },
        producer: {
            active: true,
            type: MOTION_SENSOR
        }
    },
    [templateTypes.contactSensorOpenedHeatingMode]: {
        consumer: {
            propertiesList: getConsumerPropertiesList(HEATING),
            types: [HEATING]
        },
        producer: {
            active: true,
            type: CONTACT_SENSOR
        }
    },
    [templateTypes.contactSensorClosedHeatingMode]: {
        consumer: {
            propertiesList: getConsumerPropertiesList(HEATING),
            types: [HEATING]
        },
        producer: {
            active: false,
            type: CONTACT_SENSOR
        }
    },
    [templateTypes.hiveButtonPressedActivePlugOnOff]: {
        consumer: {
            propertiesList: getConsumerPropertiesList(ACTIVE_PLUG),
            types: [ACTIVE_PLUG]
        },
        producer: {
            active: false,
            type: HIVE_BUTTON
        }
    },
    [templateTypes.hiveButtonDoublePressedActivePlugOnOff]: {
        consumer: {
            propertiesList: getConsumerPropertiesList(ACTIVE_PLUG),
            types: [ACTIVE_PLUG]
        },
        producer: {
            active: false,
            type: HIVE_BUTTON
        }
    },
    [templateTypes.hiveButtonLongHoldPressedActivePlugOnOff]: {
        consumer: {
            propertiesList: getConsumerPropertiesList(ACTIVE_PLUG),
            types: [ACTIVE_PLUG]
        },
        producer: {
            active: false,
            type: HIVE_BUTTON
        }
    },
    [templateTypes.hiveButtonPressedLightOnOff]: {
        consumer: {
            propertiesList: getConsumerPropertiesList(BULB),
            types: bulbTypes
        },
        producer: {
            active: false,
            type: HIVE_BUTTON
        }
    },
    [templateTypes.hiveButtonDoublePressedLightOnOff]: {
        consumer: {
            propertiesList: getConsumerPropertiesList(BULB),
            types: bulbTypes
        },
        producer: {
            active: false,
            type: HIVE_BUTTON
        }
    },
    [templateTypes.hiveButtonLongHoldPressedLightOnOff]: {
        consumer: {
            propertiesList: getConsumerPropertiesList(BULB),
            types: bulbTypes
        },
        producer: {
            active: false,
            type: HIVE_BUTTON
        }
    },
    [templateTypes.hiveButtonPressedHeatingMode]: {
        consumer: {
            propertiesList: getConsumerPropertiesList(HEATING),
            types: [HEATING]
        },
        producer: {
            active: false,
            type: HIVE_BUTTON
        }
    },
    [templateTypes.hiveButtonDoublePressedHeatingMode]: {
        consumer: {
            propertiesList: getConsumerPropertiesList(HEATING),
            types: [HEATING]
        },
        producer: {
            active: false,
            type: HIVE_BUTTON
        }
    },
    [templateTypes.hiveButtonLongHoldPressedHeatingMode]: {
        consumer: {
            propertiesList: getConsumerPropertiesList(HEATING),
            types: [HEATING]
        },
        producer: {
            active: false,
            type: HIVE_BUTTON
        }
    }
};



// WEBPACK FOOTER //
// ./src/core/utils/recipes/index.js